import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { Settings, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import useAuthStore from '../../store/authStore';
import { changePasswordAPI } from '../../services/authService';
import { changePasswordSchema } from '../../utils/validationSchemas';

const ChangePasswordPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (values, { setSubmitting, setStatus, resetForm }) => {
    try {
      setStatus(null);

      const passwordData = {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      };

      await changePasswordAPI(user.id, passwordData);
      
      setStatus({ success: true });
      resetForm();

      // Redirect to admin dashboard after 2 seconds
      setTimeout(() => {
        navigate('/admin', { replace: true });
      }, 2000);
    } catch (err) {
      setStatus({ error: err.message || 'Failed to change password. Please check your current password.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 pt-32">
      <div className="w-full max-w-md bg-white rounded-[2.5rem] p-10 shadow-2xl border border-slate-100 space-y-8">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center text-subidha-blue mx-auto">
            <Settings className="w-8 h-8" />
          </div>
          <h2 className="text-3xl font-serif-brand font-black text-slate-900">Change Password</h2>
          <p className="text-slate-500 font-medium">Update your security credentials.</p>
        </div>
        
        <Formik
          initialValues={{
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
          }}
          validationSchema={changePasswordSchema}
          onSubmit={handleSubmit}
          validateOnChange={true}
          validateOnBlur={true}
        >
          {({ isSubmitting, status, touched, errors }) => (
            <Form className="space-y-6">
              {status?.error && (
                <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-red-900">Error</p>
                    <p className="text-xs text-red-700 mt-1">{status.error}</p>
                  </div>
                </div>
              )}

              {status?.success && (
                <div className="bg-green-50 border border-green-200 rounded-2xl p-4 flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-green-900">Success</p>
                    <p className="text-xs text-green-700 mt-1">Password changed successfully! Redirecting...</p>
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <label htmlFor="currentPassword" className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">
                  Current Password
                </label>
                <Field
                  id="currentPassword"
                  name="currentPassword"
                  type="password"
                  autoComplete="current-password"
                  disabled={isSubmitting}
                  className={`w-full px-5 py-4 bg-slate-50 border rounded-2xl focus:ring-4 focus:ring-accent-green/20 outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                    touched.currentPassword && errors.currentPassword ? 'border-red-300' : 'border-slate-200'
                  }`}
                  placeholder="••••••••"
                />
                <ErrorMessage name="currentPassword" component="div" className="text-xs text-red-600 mt-1 px-1" />
              </div>

              <div className="space-y-2">
                <label htmlFor="newPassword" className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">
                  New Password
                </label>
                <Field
                  id="newPassword"
                  name="newPassword"
                  type="password"
                  autoComplete="new-password"
                  disabled={isSubmitting}
                  className={`w-full px-5 py-4 bg-slate-50 border rounded-2xl focus:ring-4 focus:ring-accent-green/20 outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                    touched.newPassword && errors.newPassword ? 'border-red-300' : 'border-slate-200'
                  }`}
                  placeholder="••••••••"
                />
                <ErrorMessage name="newPassword" component="div" className="text-xs text-red-600 mt-1 px-1" />
              </div>

              <div className="space-y-2">
                <label htmlFor="confirmPassword" className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">
                  Confirm New Password
                </label>
                <Field
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  disabled={isSubmitting}
                  className={`w-full px-5 py-4 bg-slate-50 border rounded-2xl focus:ring-4 focus:ring-accent-green/20 outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                    touched.confirmPassword && errors.confirmPassword ? 'border-red-300' : 'border-slate-200'
                  }`}
                  placeholder="••••••••"
                />
                <ErrorMessage name="confirmPassword" component="div" className="text-xs text-red-600 mt-1 px-1" />
              </div>

              <button 
                type="submit" 
                disabled={isSubmitting || status?.success}
                className="w-full bg-subidha-blue hover:bg-slate-900 text-white py-5 rounded-2xl font-black transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 flex items-center justify-center space-x-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>UPDATING...</span>
                  </>
                ) : (
                  <span>UPDATE PASSWORD</span>
                )}
              </button>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
